import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum, RumInitConfiguration } from "@datadog/browser-rum";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Store } from "@ngxs/store";
import _ from "lodash";
import { BehaviorSubject, EMPTY, Observable } from "rxjs";
import { catchError } from "rxjs/operators";
import HttpContextTokens from "src/app/_intercepters/http-context-tokens";
import UserState from "src/app/states/user/user.state";
import { APP_CONFIG_NAME, AppConfig } from "src/environments/app.config";
import { environment } from "src/environments/environment";
import { SellingChannelsService } from "../settings/selling-channels.service";
import { ToastrService } from "ngx-toastr";

@UntilDestroy({ checkProperties: true })
@Injectable({
  providedIn: "root",
})
export default class AppInitService {
  private appConfigSubject = new BehaviorSubject<AppConfig>(null);

  constructor(
    private http: HttpClient,
    private stateStore: Store,
    private sellingChannelsService: SellingChannelsService,
    private toastr: ToastrService
  ) {
    this.init();
  }

  public get appConfig$(): Observable<AppConfig> {
    return this.appConfigSubject.asObservable();
  }

  public get appConfig() {
    return this.appConfigSubject.getValue();
  }

  private set appConfig(value: AppConfig) {
    this.appConfigSubject.next(value);
    window[APP_CONFIG_NAME] = value;
    this.configure();
  }

  private init() {
    this.appConfig = window[APP_CONFIG_NAME] as AppConfig;

    const context = new HttpContext()
      .set(HttpContextTokens.SKIP_ERROR_HANDLING, true)
      .set(HttpContextTokens.MAX_RETRY_OVERRIDE, Infinity);
    this.http
      .get<AppConfig>(`${environment.baseUrl}/configurations`, { context })
      .pipe(
        catchError((err: unknown) => {
          console.error(err);
          return EMPTY;
        }),
        untilDestroyed(this)
      )
      .subscribe((remoteConfig) => {
        const finalConfig = _.mergeWith(
          { isInitialized: true } as AppConfig,
          window[APP_CONFIG_NAME],
          remoteConfig,
          (appConfigValue: AppConfig, windowConfigValue: AppConfig) => appConfigValue || windowConfigValue
        ) as AppConfig;
        this.appConfig = finalConfig;
      });
  }

  private configure() {
    if (this.appConfig.datadogClientId) {
      this.initDatadogRum();
      this.initDatadogLogs();
      this.stateStore
        .select(UserState)
        .pipe(untilDestroyed(this))
        .subscribe(UserState.setUserToDatadog);
    }
  }

  /**
   * Builds the Walmart authorization URL used for connecting a Walmart store.
   * Returns null if the Walmart client configuration is missing.
   */
  public buildWalmartAuthorizeUrl(redirectUri: string): string | null {
    const clientId = this.appConfig?.walmartClientId;
    if (!clientId) {
      return null;
    }

    const state = this.generateRandomString(16);
    const nonce = this.generateRandomString(16);
    const clientType = 'seller';

    const urlParams = new URLSearchParams();
    urlParams.set('responseType', 'code');
    urlParams.set('clientId', clientId);
    urlParams.set('redirectUri', redirectUri);
    urlParams.set('clientType', clientType);
    urlParams.set('nonce', nonce);
    urlParams.set('state', state);

    return `https://login.account.wal-mart.com/authorize?${urlParams.toString()}`;
  }

  /**
   * Opens the Walmart authorization popup, waits for the callback, and then
   * calls the backend to create/update the Walmart store connection.
   */
  public connectWalmartWithPopup(options: {
    storeName?: string;
    onlyActiveProducts: boolean;
    storeId?: number | null;
    redirectUri: string;
    isTestAccount: boolean;
    determineStoreNameInBackend?: boolean;
    currencyId?: number;
    setLoading?: (loading: boolean) => void;
    onSuccess?: () => void;
  }): void {
    const authUrl = this.buildWalmartAuthorizeUrl(options.redirectUri);
    if (!authUrl) {
      this.toastr.error('Walmart client configuration is missing. Please contact support.', 'Configuration Error');
      return;
    }

    const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
    const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;
    const width = window.innerWidth ? window.innerWidth : document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width;
    const height = window.innerHeight ? window.innerHeight : document.documentElement.clientHeight ? document.documentElement.clientHeight : screen.height;
    const systemZoom = width / window.screen.availWidth;
    const left = (width - 1000) / 2 / systemZoom + dualScreenLeft;
    const top = (height - 500) / 2 / systemZoom + dualScreenTop;

    const win = window.open(authUrl, '_blank', 'scrollbars=yes, width=' + 1000 / systemZoom + ', height=' + 500 / systemZoom + ', top=' + top + ', left=' + left);
    if (win && window.focus) {
      win.focus();
    }

    const pollTimer = window.setInterval(() => {
      try {
        if (!win || win.closed) {
          window.clearInterval(pollTimer);
          return;
        }
        const currentUrl = win.document.URL;
        if (currentUrl && currentUrl.includes('sellingchannels?code=')) {
          window.clearInterval(pollTimer);
          const code = this.getUrlParam(currentUrl, 'code');
          const sellerId = this.getUrlParam(currentUrl, 'sellerId');
          win.close();
          options.setLoading?.(true);
          this.sellingChannelsService.createWalmartStoreConnection({
            name: options.storeName,
            onlyActiveProducts: options.onlyActiveProducts,
            id: options.storeId ?? null,
            code,
            redirectUri: options.redirectUri,
            sellerId,
            isTestAccount: options.isTestAccount,
            determineStoreNameInBackend: options.determineStoreNameInBackend,
            currencyId: options.currencyId
          }).subscribe(() => {
            options.setLoading?.(false);
            const nameForMessage = options.storeName || 'Walmart';
            this.toastr.success(nameForMessage + ' is connected', 'Ok');
            options.onSuccess?.();
          }, (err) => {
            options.setLoading?.(false);
            this.toastr.error(err?.error || 'Failed to connect Walmart store', 'Error');
          });
        }
      } catch (e) {
        // cross-origin access before redirect back, ignore
      }
    }, 500);
  }

  private initDatadogRum() {
    datadogRum.init({
      applicationId: this.appConfig.datadogApplicationId,
      clientToken: this.appConfig.datadogClientId,
      site: "datadoghq.com",
      service: "web-app",
      env: this.appConfig.serverEnv,
      version: this.appConfig.version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      traceSampleRate: 100,
      telemetrySampleRate: 20,
      trackUserInteractions: true,
      trackFrustrations: true,
      trackResources: true,
      trackLongTasks: true,
      trackSessionAcrossSubdomains: true,
      useSecureSessionCookie: true,
      useCrossSiteSessionCookie: true,
      allowFallbackToLocalStorage: true,
      silentMultipleInit: true,
      defaultPrivacyLevel: "allow",
      allowedTracingUrls: [
        /https:\/\/.*\.inventoryahead\.com/,
        /http(s)?:\/\/localhost/,
      ],
    } as RumInitConfiguration);
    datadogRum.startSessionReplayRecording();
  }

  private initDatadogLogs() {
    datadogLogs.init({
      clientToken: this.appConfig.datadogClientId,
      site: "datadoghq.com",
      service: "web-app",
      env: this.appConfig.serverEnv,
      version: this.appConfig.version,
      forwardErrorsToLogs: true,
      forwardReports: "all",
      sessionSampleRate: 100,
      telemetrySampleRate: 20,
      silentMultipleInit: true,
      trackSessionAcrossSubdomains: true,
      useSecureSessionCookie: true,
      useCrossSiteSessionCookie: true,
    });
  }

  private generateRandomString(length: number): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const array = new Uint32Array(length);
    if (window.crypto && window.crypto.getRandomValues) {
      window.crypto.getRandomValues(array);
    } else {
      for (let i = 0; i < length; i++) {
        array[i] = Math.floor(Math.random() * chars.length);
      }
    }
    return Array.from(array).map(x => chars[x % chars.length]).join('');
  }

  //credits: http://www.netlobo.com/url_query_string_javascript.html
  private getUrlParam(url: string, name: string): string {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regexS = '[\\#&?]' + name + '=([^&#]*)';
    const regex = new RegExp(regexS);
    const results = regex.exec(url);
    if (results == null) {
      return '';
    } else {
      return results[1];
    }
  }
}
